import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { usePageMeta } from "@/lib/seo";
import { STORE_ADDRESS } from "@/constants/store";
import ProductCard from "@/components/shop/ProductCard";
import { Sparkles, Flame, Tag, ShoppingBag } from "lucide-react";

const PRICE_TIERS = [
  { id: "all",  label: "ALL Under $20", max: 20 },
  { id: "1",    label: "$1 Deals",       max: 1,   exact: true },
  { id: "2",    label: "$2 Deals",       max: 2,   exact: true },
  { id: "5",    label: "Under $5",       max: 5 },
  { id: "10",   label: "Under $10",      max: 10 },
  { id: "20",   label: "Under $20",      max: 20 },
];

export default function DealsUnder20Page() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tier = searchParams.get("tier") || "all";
  const activeTier = PRICE_TIERS.find((t) => t.id === tier) || PRICE_TIERS[0];

  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ count: 0, lowest: null, highest: null });

  usePageMeta(
    "Deals Under $20 · Closeout Steals at IPVALUE",
    "Brand-new closeout & overstock items priced at $1, $2, $5, $10 and $20 at IPVALUE MARKETPLACE — beauty, snacks, tools, household goods and more. Serving Grand Rapids and West Michigan from Grand Rapids, MI. Pickup or local delivery."
  );

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const params = {
          max_price: activeTier.max,
          limit: 200,
          sort: "price_asc",
        };
        if (activeTier.exact && activeTier.id !== "all") {
          // For "$1" and "$2" tiers, also include products at exactly that price
          params.min_price = activeTier.max;
        }
        const { data } = await api.get("/products", { params });
        if (!cancelled) {
          setProducts(data.items || []);
          if (data.items?.length) {
            const prices = data.items.map((p) => p.price);
            setStats({
              count: data.items.length,
              lowest: Math.min(...prices),
              highest: Math.max(...prices),
            });
          } else {
            setStats({ count: 0, lowest: null, highest: null });
          }
        }
      } catch {
        if (!cancelled) setProducts([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTier.id, activeTier.max, activeTier.exact]);

  return (
    <div data-testid="deals-under-20-page">
      {/* HERO — high-contrast yellow/blue to make the deals feel hot */}
      <section className="bg-gradient-to-br from-[#FFC220] via-[#FFD24D] to-[#FFC220] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute -top-4 -left-4 w-72 h-72 bg-[#0A1C40] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-[#0A1C40] rounded-full blur-3xl" />
        </div>
        <div className="relative max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-14 lg:py-20">
          <div className="inline-flex items-center gap-2 bg-[#0A1C40] text-white text-xs font-bold tracking-[0.2em] uppercase px-4 py-2" data-testid="deals-eyebrow">
            <Flame className="w-3 h-3 text-[#FFC220]" /> Closeout · Brand New · Wholesale-Priced
          </div>
          <h1 className="font-display font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter mt-5 text-[#0A1C40] leading-[0.95]" data-testid="deals-title">
            Everything<br />
            <span className="relative inline-block">
              <span className="relative z-10">Under $20</span>
              <span className="absolute inset-x-0 bottom-1 h-3 bg-white/40 -z-0" />
            </span>
          </h1>
          <p className="mt-6 text-lg lg:text-xl text-[#0A1C40]/85 max-w-2xl font-medium">
            Brand-new closeout &amp; overstock — $1, $2, $5, $10 and $20 finds.
            Beauty, snacks, tools, household. Same Grand Rapids, MI showroom — serving Grand Rapids &amp; West Michigan.
            Pick up today at {STORE_ADDRESS.short}.
          </p>
          <div className="mt-8 inline-flex flex-wrap gap-2" data-testid="deals-stats">
            {stats.count > 0 && (
              <span className="bg-[#0A1C40] text-white px-4 py-2 text-sm font-bold inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-[#FFC220]" /> {stats.count} item{stats.count !== 1 ? "s" : ""} in stock
              </span>
            )}
            {stats.lowest !== null && stats.lowest < 5 && (
              <span className="bg-white border-2 border-[#0A1C40] text-[#0A1C40] px-4 py-2 text-sm font-bold">
                Starting at ${stats.lowest.toFixed(2)}
              </span>
            )}
          </div>
        </div>
      </section>

      {/* PRICE TIER TABS */}
      <section className="border-b border-gray-200 bg-white sticky top-0 z-30 shadow-sm">
        <div className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-4 overflow-x-auto">
          <div className="flex gap-2 min-w-max" data-testid="deals-price-tiers">
            {PRICE_TIERS.map((t) => {
              const active = t.id === activeTier.id;
              return (
                <button
                  key={t.id}
                  onClick={() => {
                    const next = new URLSearchParams(searchParams);
                    if (t.id === "all") next.delete("tier"); else next.set("tier", t.id);
                    setSearchParams(next, { replace: true });
                  }}
                  className={`whitespace-nowrap px-5 py-2.5 text-sm font-bold tracking-wide border-2 transition-all ${
                    active
                      ? "bg-[#0A1C40] text-white border-[#0A1C40]"
                      : "bg-white text-[#0A1C40] border-gray-200 hover:border-[#0A1C40]"
                  }`}
                  data-testid={`deals-tier-${t.id}`}
                >
                  <Tag className={`w-3.5 h-3.5 inline-block mr-1.5 -mt-0.5 ${active ? "text-[#FFC220]" : "text-[#C9971E]"}`} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* PRODUCT GRID */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-12 lg:px-24 py-10">
        {loading ? (
          <div className="text-sm text-gray-500 text-center py-20" data-testid="deals-loading">Loading deals…</div>
        ) : products.length === 0 ? (
          <div className="border border-dashed border-gray-300 p-16 text-center" data-testid="deals-empty">
            <Tag className="w-10 h-10 text-gray-300 mx-auto" />
            <div className="font-display font-black text-2xl mt-4">No {activeTier.label.toLowerCase()} right now</div>
            <p className="text-gray-500 mt-2 max-w-md mx-auto">
              We're constantly adding new closeout pallets. Check back tomorrow — or shop our other categories below.
            </p>
            <Link to="/shop" className="mt-6 inline-flex items-center gap-2 bg-[#0A1C40] text-white font-bold px-5 py-2.5 hover:bg-[#0E2756]" data-testid="deals-shop-all">
              <ShoppingBag className="w-4 h-4" /> Shop everything
            </Link>
          </div>
        ) : (
          <>
            <div className="flex items-baseline justify-between mb-6">
              <h2 className="font-display font-black text-2xl tracking-tight">
                {activeTier.label}{" "}
                <span className="text-gray-400 text-base font-normal">— {products.length} item{products.length !== 1 ? "s" : ""}</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 lg:gap-6" data-testid="deals-product-grid">
              {products.map((p) => (
                <ProductCard key={p.id} product={p} />
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
